import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import axios from 'axios';
import { api } from '../lib/api.ts';

export type Role = 'SYS_ADMIN' | 'ADMIN' | 'STAFF' | 'STOCKKEEPER' | 'FINANCE';

export interface Me {
  id: string;
  email: string;
  name: string;
  /** Thaana name from Office — preferred over `name` on every display surface. */
  nameDv?: string | null;
  role: Role;
  orgId: string | null;
  isOrgAdmin: boolean;
  designation?: string | null;
}

interface AuthResponse { accessToken: string; refreshToken: string; user: Me }

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: Me | null;
  login: (email: string, password: string) => Promise<void>;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
  hydrateMe: () => Promise<void>;
  ssoBootstrap: (force?: boolean) => Promise<boolean>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      login: async (email, password) => {
        const r = await axios.post<AuthResponse>('/api/auth/login', { email, password });
        set({ accessToken: r.data.accessToken, refreshToken: r.data.refreshToken, user: r.data.user });
      },
      refresh: async () => {
        const t = get().refreshToken;
        if (!t) throw new Error('no refresh token');
        const r = await axios.post<AuthResponse>('/api/auth/refresh', { refreshToken: t });
        set({ accessToken: r.data.accessToken, refreshToken: r.data.refreshToken, user: r.data.user });
      },
      logout: async () => {
        const t = get().refreshToken;
        // withCredentials so the parent-domain wap_sso cookie rides along and the
        // server can revoke the Office shared session + clear the cookie.
        try {
          await axios.post('/api/auth/logout', { refreshToken: t ?? undefined }, { withCredentials: true });
        } catch { /* non-fatal — still clear local state + navigate */ }
        set({ accessToken: null, refreshToken: null, user: null });
        if (typeof window !== 'undefined') {
          window.location.assign('/login');
        }
      },
      hydrateMe: async () => {
        if (!get().accessToken) return;
        try {
          const r = await api.get<{ user: Me }>('/users/me');
          set({ user: r.data.user });
        } catch { /* swallow — refresh will fire on next call */ }
      },
      // Ambient-SSO cold-boot: with no local STAFF session, ask the server to
      // resolve the parent-domain wap_sso cookie into a session. withCredentials
      // so the cookie is sent. Returns true if a session was established (store
      // updated exactly like login), false on 204/absent/error → render login as
      // normal. Vendor no-login links use a separate store and are untouched.
      ssoBootstrap: async (force = false) => {
        if (!force && get().accessToken) return true;
        try {
          const r = await axios.post<AuthResponse | ''>('/api/auth/sso-bootstrap', {}, { withCredentials: true });
          if (r.status === 204 || !r.data || !(r.data as AuthResponse).accessToken) return false;
          const d = r.data as AuthResponse;
          set({ accessToken: d.accessToken, refreshToken: d.refreshToken, user: d.user });
          return true;
        } catch {
          return false;
        }
      },
    }),
    { name: 'wappro.auth' },
  ),
);
